<template>
  <CModal
    :show.sync="visible"
    size="xl"
    :title="modalTitle"
  >
    <div v-if="collaborator">
      <PTabs
        :tabs="tabs"
        :selected="activeTab"
        @select="activeTab = $event"
        pills
        class="mb-3"
      />
      
      <div v-if="activeTab === 'all'" class="tab-content">
        <div v-if="allPermissionsLoading" class="text-center py-4">
          <CSpinner color="primary" />
          <div class="mt-2 text-muted">
            <translate translate-context="permissions.modal">Loading permissions...</translate>
          </div>
        </div>
        <div v-else-if="formattedPermissions">
          <div class="search-box mb-3">
            <CInput
              v-model="searchTerm"
              :placeholder="$gettext('Search permissions...')"
            >
              <template #prepend>
                <CIcon name="cilSearch" />
              </template>
            </CInput>
          </div>

          <Permission
            :permissions="formattedPermissions"
            :active="formattedPermissions"
            :search-filter="searchTerm"
          />
        </div>
        <div v-else class="text-center text-muted py-4">
          <translate translate-context="permissions.modal">No permissions found</translate>
        </div>
      </div>

      <div v-if="activeTab === 'ftp'" class="tab-content">
        <FtpCredentialDisplay
          v-if="collaborator && collaborator.ftp_credentials"
          :credentials="collaborator.ftp_credentials"
        />
        <div v-else class="text-center text-muted py-4">
          <translate translate-context="permissions.modal">No FTP credentials available</translate>
        </div>
      </div>

      <div v-if="activeTab.startsWith('role-')" class="tab-content">
        <div v-for="(role, index) in collaborator.roles" :key="index">
          <template v-if="activeTab === `role-${index}`">
            <div v-if="role.permissions">
              <div class="role-info mb-3">
                <div v-if="role.is_inherited" class="d-flex align-items-center mb-2">
                  <CIcon name="cilArrowCircleBottom" size="sm" class="mr-2 text-muted" />
                  <small class="text-muted">
                    <translate translate-context="permissions.modal">Inherited</translate>
                  </small>
                </div>
                <div v-if="role.is_pending" class="alert alert-warning">
                  <CIcon name="cilWarning" class="mr-2" />
                  <translate translate-context="permissions.modal">This role is pending - the user hasn't accepted the invitation yet</translate>
                </div>
                <div v-if="role.expires_at" class="alert alert-warning">
                  <CIcon name="cilClock" class="mr-2" />
                  <translate translate-context="permissions.modal">Expires at:</translate> {{ formatDate(role.expires_at) }}
                </div>
              </div>
              
              <div class="search-box mb-3">
                <CInput
                  v-model="roleSearchTerms[index]"
                  :placeholder="$gettext('Search permissions...')"
                >
                  <template #prepend>
                    <CIcon name="cilSearch" />
                  </template>
                </CInput>
              </div>
              
              <Permission
                :permissions="formatRolePermissions(role.permissions)"
                :active="formatRolePermissions(role.permissions)"
                :search-filter="roleSearchTerms[index] || ''"
              />
            </div>
            <div v-else class="text-center text-muted py-4">
              <translate translate-context="permissions.modal">No permissions found for this role</translate>
            </div>
          </template>
        </div>
      </div>
    </div>
    
    <template #footer>
      <CButton color="secondary" @click="visible = false">
        <translate>Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import Permission from '@/domain/roles/components/Permission.vue'
import PTabs from '@/domain/core/components/PTabs.vue'
import FtpCredentialDisplay from './FtpCredentialDisplay.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'UserPermissionModal',

  components: {
    Permission,
    PTabs,
    FtpCredentialDisplay
  },
  
  props: {
    scope: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      visible: false,
      collaborator: null,
      searchTerm: '',
      roleSearchTerms: {},
      activeTab: 'all',
      allPermissionsLoading: false
    }
  },
  
  computed: {
    ...mapGetters('collaborators', ['collaboratorPermissions']),

    modalTitle() {
      if (this.collaborator) {
        return this.$gettext('Permissions for %{email}').replace('%{email}', this.collaborator.email)
      }
      return this.$gettext('User Permissions')
    },

    canViewFtpCredentials() {
      const scopeType = this.scope?.type
      if (scopeType === 'pica-service') {
        return this.hasPerm('collaborator.export_credentials')
      } else if (scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.export_credentials')
      }
      return this.hasPerm('admin.collaborator.export_credentials')
    },
    
    tabs() {
      const tabs = [
        {
          name: 'all',
          label: this.$gettext('All Permissions'),
          icon: 'cilShieldAlt'
        }
      ]

      // Add FTP Credentials tab if available and user has permission
      if (this.collaborator && this.collaborator.ftp_credentials && this.canViewFtpCredentials) {
        tabs.push({
          name: 'ftp',
          label: this.$gettext('FTP Credentials'),
          icon: 'cilCloudDownload'
        })
      }

      if (this.collaborator && this.collaborator.roles) {
        this.collaborator.roles.forEach((role, index) => {
          let label = role.display_name || role.name

          // Add warning indicator for pending roles
          if (role.is_pending) {
            label = `⚠️ ${label}`
          }

          tabs.push({
            name: `role-${index}`,
            label: label,
            icon: role.is_inherited ? 'cilArrowCircleBottom' : 'cilShieldAlt',
            isPending: role.is_pending
          })
        })
      }

      return tabs
    },
    
    formattedPermissions() {
      if (!this.collaboratorPermissions || !this.collaboratorPermissions.permissions) return null
      
      // The new API returns permissions in a standard format
      return this.formatRolePermissions(this.collaboratorPermissions.permissions)
    }
  },
  
  methods: {
    ...mapActions('collaborators', ['fetchCollaboratorPermissions', 'fetchCollaboratorDetail']),

    async show(collaborator) {
      this.collaborator = collaborator
      this.searchTerm = ''
      this.roleSearchTerms = {}
      this.activeTab = 'all'
      this.visible = true

      // Load both permissions and FTP credentials
      await Promise.all([
        this.loadAllPermissions(collaborator.id),
        this.loadCollaboratorDetail(collaborator.id)
      ])
    },

    async loadCollaboratorDetail(collaboratorId) {
      try {
        // Fetch full collaborator details including ftp_credentials
        const detail = await this.fetchCollaboratorDetail(collaboratorId)
        if (detail) {
          // Merge detail data with existing collaborator (keeps permissions data)
          this.collaborator = {
            ...this.collaborator,
            ...detail
          }
        }
      } catch (error) {
        // Error loading FTP credentials - non-blocking
      }
    },
    
    async loadAllPermissions(collaboratorId) {
      this.allPermissionsLoading = true

      try {
        // Use Vuex action to fetch and store permissions
        const response = await this.fetchCollaboratorPermissions(collaboratorId)
        // Merge permissions data with existing collaborator (keeps ftp_credentials)
        if (response) {
          this.collaborator = {
            ...this.collaborator,
            ...response
          }
        }
      } catch (error) {
        // Error loading permissions
      } finally {
        this.allPermissionsLoading = false
      }
    },
    
    formatRolePermissions(permissions) {
      if (!permissions) return null
      
      // Handle special case for wildcard permissions
      if (permissions['*'] === true) {
        return { 'All Permissions': true }
      }
      
      // If permissions is already an object with nested structure, return as is
      if (typeof permissions === 'object') {
        return permissions
      }
      
      return null
    },
    
    hide() {
      this.visible = false
      this.collaborator = null
      this.searchTerm = ''
      this.roleSearchTerms = {}
      this.activeTab = 'all'
      this.allPermissionsLoading = false
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }
  }
}
</script>

<style scoped>
.search-box {
  max-width: 400px;
}

.tab-content {
  padding: 1rem 0;
}

.role-info {
  margin-bottom: 1rem;
}

.role-info .alert {
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
}

.role-info .alert:last-child {
  margin-bottom: 0;
}
</style>